import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services } from '../mock';
import SEO from '../components/SEO';

const Services = () => {
  return (
    <div>
      <SEO
        title="Our Services"
        description="Explore Serene Sparkle's full range of cleaning services: residential, commercial, deep cleaning, end of tenancy, and more."
        keywords="cleaning services, residential cleaning, commercial cleaning, deep cleaning, end of tenancy"
        canonical="/services"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-sky-50 to-emerald-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-slate-900 mb-6">Our Services</h1>
            <p className="text-xl text-slate-600">
              Comprehensive cleaning solutions tailored to create peaceful, spotless spaces for homes and businesses
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="space-y-12">
            {services.map((service, index) => (
              <Card key={service.id} className="border-sky-100 overflow-hidden hover:shadow-xl transition-shadow">
                <div className={`grid md:grid-cols-2 gap-0 ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`}>
                  <div className={`${index % 2 === 1 ? 'md:col-start-2' : ''}`}>
                    <img
                      src={`${service.image}?w=800&q=75&auto=format`}
                      alt={`${service.title} - Professional cleaning by Serene Sparkle`}
                      className="w-full h-full object-cover min-h-[300px]"
                      loading="lazy"
                      width="800"
                      height="500"
                    />
                  </div>
                  <CardContent className="p-8 flex flex-col justify-center">
                    <h2 className="text-3xl font-bold text-slate-900 mb-4">{service.title}</h2>
                    <p className="text-lg text-slate-600 mb-6">{service.description}</p>
                    
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-slate-800 mb-3">What we do:</h3>
                      <ul className="space-y-2">
                        {service.tasks.map((task, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                            <span className="text-slate-600">{task}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="bg-sky-50 rounded-lg p-4 mb-6">
                      <p className="text-sm font-semibold text-slate-800 mb-1">Ideal for:</p>
                      <p className="text-slate-600">{service.idealFor}</p>
                    </div>

                    <div className="flex gap-3">
                      <Link to={`/services/${service.id}`} className="flex-1">
                        <Button className="w-full bg-sky-600 hover:bg-sky-700">
                          View {service.title} Details <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                      <Link to="/contact" className="flex-1">
                        <Button variant="outline" className="w-full border-sky-600 text-sky-600 hover:bg-sky-50">
                          Request a Free Quote
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>

          {/* Quote CTA */}
          <div className="mt-12 text-center bg-gradient-to-br from-sky-50 to-emerald-50 rounded-lg p-8">
            <p className="text-lg text-slate-700 mb-4">
              <span className="font-semibold">Need a custom solution?</span> We provide bespoke cleaning plans tailored to your unique needs.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-sky-600 hover:bg-sky-700">
                Contact Us for a Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
